import { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { useNavigate } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import logoHeader from "../../assets/logoHeader.png"
import Footer from "../../components/footer/Footer"
import { useAuth } from "../../hooks/useAuth"

interface LoginFormData {
    username: string
    password: string
}

export default function LoginScreen() {
    const [formData, setFormData] = useState<LoginFormData>({
        username: "",
        password: ""
    })
    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    const navigate = useNavigate()
    const { login, isLoading, error } = useAuth()

    useEffect(() => {
        if (error) {
            setErrors({})
        }
    }, [error])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }))

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: ""
            }))
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!formData.username) {
            newErrors.username = "Usuario é obrigatório"
        }

        if (!formData.password) {
            newErrors.password = "Senha é obrigatória"
        } else if (formData.password.length < 6) {
            newErrors.password = "Senha deve ter pelo menos 6 caracteres"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        await login({
            user: formData.username,
            password: formData.password
        })
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSubmit(e as any)
        }
    }

    const handleBackToTracking = () => {
        navigate("/rastreamento")
    }

    return (
        <>
            <Helmet>
                <title>LogPlace - Login</title>
                <meta
                    name="description"
                    content="Faça login para acessar o rastreamento em massa da LogPlace"
                />
                <meta
                    name="keywords"
                    content="login, rastreamento em massa, logística, LogPlace"
                />
                <meta name="author" content="LogPlace" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta
                    property="og:title"
                    content="Login - Rastreamento em Massa"
                />
                <meta
                    property="og:description"
                    content="Acesse o rastreamento em massa da LogPlace"
                />
                <meta property="og:url" content="https://logplace.com.br" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="relative flex flex-col w-full min-h-screen bg-white">
                <ToastContainer
                    position="bottom-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    icon={false}
                    limit={1}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />

                {/* Header */}
                <div className="flex items-center justify-center w-full h-28">
                    <button
                        onClick={handleBackToTracking}
                        className="flex items-center justify-center h-24 bg-white rounded-lg w-72"
                    >
                        <img
                            className="w-[100%] h-[90%]"
                            src={logoHeader}
                            alt="LogPlace Logo"
                        />
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-1 px-4 xl:px-44 py-8 bg-gradient-to-b from-white from-1% via-secondary-1 via-30% via-secondary-2 via-30% to-primary to-80%">
                    <div className="max-w-md mx-auto">
                        {/* Login Card */}
                        <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 border border-gray-100">
                            <div className="text-center mb-8">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-secondary-1/20 to-secondary-2/20 mb-4">
                                    <svg
                                        className="h-8 w-8 text-secondary-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                    </svg>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                                    Login
                                </h2>
                                <p className="text-gray-600 text-sm sm:text-base">
                                    Digite suas credenciais para continuar
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Email Field */}
                                <div>
                                    <label
                                        htmlFor="username"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Usuário
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg
                                                className="h-5 w-5 text-gray-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            id="username"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            onKeyPress={handleKeyPress}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-secondary-1 focus:border-transparent transition-all duration-200 ${
                                                errors.username
                                                    ? "border-red-500 bg-red-50"
                                                    : "border-gray-300 hover:border-gray-400"
                                            }`}
                                            placeholder="Digite seu usuário"
                                            disabled={isLoading}
                                            autoComplete="username"
                                            autoFocus
                                        />
                                    </div>
                                    {errors.username && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <svg
                                                className="w-4 h-4 mr-1"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            {errors.username}
                                        </p>
                                    )}
                                </div>

                                {/* Password Field */}
                                <div>
                                    <label
                                        htmlFor="password"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Senha
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg
                                                className="h-5 w-5 text-gray-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                />
                                            </svg>
                                        </div>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            onKeyPress={handleKeyPress}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-secondary-1 focus:border-transparent transition-all duration-200 ${
                                                errors.password
                                                    ? "border-red-500 bg-red-50"
                                                    : "border-gray-300 hover:border-gray-400"
                                            }`}
                                            placeholder="Sua senha"
                                            disabled={isLoading}
                                            autoComplete="current-password"
                                        />
                                    </div>
                                    {errors.password && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <svg
                                                className="w-4 h-4 mr-1"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            {errors.password}
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-secondary-1 to-secondary-2 hover:from-secondary-2 hover:to-primary text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-1"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Entrando...
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center">
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                                                />
                                            </svg>
                                            Entrar
                                        </div>
                                    )}
                                </button>
                            </form>

                            {/* Help Text */}
                            <div className="mt-4 text-center">
                                <p className="text-xs text-gray-500">
                                    Precisa de ajuda? Entre em contato com o
                                    suporte
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    )
}
